import { FlatNode } from '../pages/mindmap/types';
import { getBottomRightCoords } from './get-bottom-right-coord';
import { getTopLeftCoords } from './get-top-left-coord';

/**
 * Returns the node that shares vertical space and is closest on the X-Axis to the selected node.
 * @param selectedNodeId The node from which another node should be searched.
 * @param flatPositionedNodes The positioned nodes that should be searched in.
 * @param left Is true left nodes are searched in, if false right nodes are searched in.
 * @returns The id of the horizontally nearest node.
 */
export const determineNearestHorizontalNode = (
  selectedNodeId: string,
  flatPositionedNodes: FlatNode[],
  left: boolean,
): string | undefined => {
  const selectedNode = flatPositionedNodes.find(({ id }) => id == selectedNodeId);
  if (selectedNode) {
    // filter out nodes that are on the wrong side or center around the same Y-Axis as the selected node
    let sideNodes = flatPositionedNodes.filter(({ x, width }) => {
      if (x && selectedNode.x) {
        // use 3 decimal places to always determine the nodes that center around the same Y-Axis and filter them out
        const centerX = parseFloat((x + width / 2).toFixed(3));
        const selectedCenterX = parseFloat((selectedNode.x + selectedNode.width / 2).toFixed(3));
        return left ? centerX < selectedCenterX : centerX > selectedCenterX;
      }
    });

    // get nearest horizontal node
    if (sideNodes.length > 0) {
      if (selectedNode.x && selectedNode.y) {
        let nearestNode: FlatNode | undefined = undefined;
        let nearestHorizontalDiff = Infinity;
        // if no node with a significant shared vertical space with the selected
        // node is found select the nearest unsignificant node
        let nearestUnsignificantNode: FlatNode | undefined = undefined;

        for (const node of sideNodes) {
          const selectedNodeTopLeft = getTopLeftCoords(selectedNode);
          const selectedNodeBottomRight = getBottomRightCoords(selectedNode);
          // cap height to make high nodes always select nodes that are near the top
          selectedNodeBottomRight.y = selectedNode.y + Math.min(selectedNode.height, 300);

          const nodeTopLeft = getTopLeftCoords(node);
          const nodeBottomRight = getBottomRightCoords(node);

          // horizontal difference says how far the node is away from the selected node in the X-axis
          let horizontalDiff = left
            ? selectedNodeTopLeft.x - nodeBottomRight.x
            : nodeTopLeft.x - selectedNodeBottomRight.x;

          // vertical space is the space left and right of a selected node with the selected nodes complete height
          //
          //         ███████████
          // ────────█         █───────────────────────────────────────────────────────────█▀▀▀▀▀▀▀█────────── <-- start of vertical space
          //         █         █  Ʌ                                                        █       █
          //         █    1    █  I <-- sharedVerticalSpace of node 1                      █       █  <-- selected node
          //         █         █  V     ███████████  I <-- sharedVerticalSpace of node 2   █       █
          // ────────█         █────────█         █────────────────────────────────────────▀▀▀▀▀▀▀▀▀────────── <-- end of vertical space
          //         █         █        █    2    █
          //         ███████████        █         █
          //                            █         █
          //                            ███████████

          // determine positioning of node relative to vertical space
          const startsAboveAndEndsInside =
            nodeTopLeft.y < selectedNodeTopLeft.y &&
            nodeBottomRight.y > selectedNodeTopLeft.y &&
            nodeBottomRight.y < selectedNodeBottomRight.y;
          const startsAndEndsInside =
            nodeTopLeft.y > selectedNodeTopLeft.y &&
            nodeBottomRight.y > selectedNodeTopLeft.y &&
            nodeBottomRight.y < selectedNodeBottomRight.y;
          const startsInAndEndsBelow =
            nodeTopLeft.y > selectedNodeTopLeft.y && nodeBottomRight.y > selectedNodeBottomRight.y;
          const startsAboveAndEndsBelow =
            nodeTopLeft.y < selectedNodeTopLeft.y && nodeBottomRight.y > selectedNodeBottomRight.y;

          // calculate height in px that is inside the vertical space
          let sharedVerticalSpace = 0;
          if (startsAboveAndEndsInside) {
            sharedVerticalSpace = nodeBottomRight.y - selectedNodeTopLeft.y;
          } else if (startsAndEndsInside) {
            sharedVerticalSpace = nodeBottomRight.y - nodeTopLeft.y;
          } else if (startsInAndEndsBelow) {
            sharedVerticalSpace = selectedNodeBottomRight.y - nodeTopLeft.y;
          } else if (startsAboveAndEndsBelow) {
            sharedVerticalSpace = selectedNodeBottomRight.y - selectedNodeTopLeft.y;
          }

          // a node should only be selected if a significant share if it's height is inside of the selected nodes vertical space
          const significantSharedVerticalSpace = sharedVerticalSpace > Math.min(node.height / 2, 100);

          if (!nearestNode) {
            if (significantSharedVerticalSpace) {
              nearestNode = node;
              nearestHorizontalDiff = horizontalDiff;
            } else {
              nearestUnsignificantNode = node;
            }
            continue;
          }

          const isNearerThanNearest = horizontalDiff < nearestHorizontalDiff;
          if (isNearerThanNearest) {
            if (significantSharedVerticalSpace) {
              nearestNode = node;
              nearestHorizontalDiff = horizontalDiff;
            } else {
              nearestUnsignificantNode = node;
            }
          }
        }

        if (nearestNode) return nearestNode.id;
        else if (nearestUnsignificantNode) return nearestUnsignificantNode.id;
      }
    }
  }
};
